/**
 * @module utils/str
 * @description String manipulation utility functions including token replacement,
 * random string generation, pluralization, and slugification.
 */

/**
 * @const STR_RAND_LENGTH
 * @description Default length for randomly generated strings
 * @type {number}
 */
export const STR_RAND_LENGTH = 6;

/**
 * @function makeToken
 * @description Create a token string from input by uppercasing and wrapping with separators
 * @param {string} str - Input string to tokenize
 * @param {string} [sep='##'] - Separator to wrap around token
 * @returns {string} Tokenized string (e.g., "ORDER" becomes "##ORDER##")
 */
export const makeToken = (str: string, sep = '##') => {
  return `${sep}${str.toUpperCase().replace(/\s+/g, '_')}${sep}`;
};

/**
 * @function randStr
 * @description Generate a random alphanumeric string
 * @param {number} [length=STR_RAND_LENGTH] - Length of string to generate
 * @returns {string} Random alphanumeric string
 */
export const randStr = (length: number = STR_RAND_LENGTH) => (
  Math.random().toString(36).substring(2, 2 + length)
);

/**
 * @function randSelector
 * @description Generate a random CSS-style selector string with prefix
 * @param {string} [prefix='ele'] - Prefix for the selector
 * @param {string} [sep='--'] - Separator between prefix and random part
 * @param {number} [length=STR_RAND_LENGTH] - Length of random part
 * @returns {string} Random selector (e.g., "ele--abc123")
 */
export const randSelector = (prefix = 'ele', sep = '--', length: number = STR_RAND_LENGTH) => (
  `${prefix}${sep}${randStr(length)}`
);

/**
 * @function replaceTokens
 * @description Replace all token placeholders in a string with provided values
 * @param {string} str - String containing tokens (e.g., "Order ##ORDER## on ##DATE##")
 * @param {Record<string, string>} tokens - Map of token names to replacement values
 * @returns {string} String with all tokens replaced
 */
export const replaceTokens = (str: string, tokens: Record<string, string>) => {
  let ret = str;

  for (const [key, value] of Object.entries(tokens)) {
    ret = ret.replaceAll(makeToken(key), value);
  }

  return ret;
};

/**
 * @function pluralize
 * @description Simple pluralization by adding 's' suffix when count is not 1
 * @param {number} x - Count/quantity
 * @param {string} word - Word to pluralize
 * @returns {string} Singular word if x is 1, otherwise word with 's' appended
 */
export const pluralize = (x: number, word: string) => {
  return x === 1 ? word : `${word}s`;
};

/**
 * @function slugify
 * @description Convert string to URL-safe slug format (lowercase, underscores, max 30 chars)
 * @param {string} input - String to slugify
 * @returns {string} Slugified string suitable for URLs
 */
export const slugify = (input: string) => {
  const slugged = input
    .replace(/["']/g, '')
    .replace(/[^A-Za-z0-9_]+/g, '_')
    .toLowerCase()
    .slice(0, 30);
  if (slugged.charAt(slugged.length - 1) === '_') {
    return slugged.slice(0, slugged.length - 1);
  }
  return slugged;
};
